import { computed, ref, type Ref } from 'vue';
import type { BranchMember } from '@/entities/Branch';
import type { CreateOpenTrainingPayload } from '@/entities/Training';
import { useOnboardingMinistrantes } from './useOnboardingMinistrantes';
import { useOpenTrainingExternalIndividualInstructors } from './useOpenTrainingExternalIndividualInstructors';
import { useOpenTrainingExternalCompanyInstructors } from './useOpenTrainingExternalCompanyInstructors';
import { buildMinistranteSessionPayload } from '../lib/buildMinistranteSessionPayload';

export type OpenTrainingInstructorMode = 'INTERNAL' | 'EXTERNAL';
export type OpenTrainingExternalType = 'EXTERNAL_INDIVIDUAL' | 'EXTERNAL_COMPANY';

export type OpenTrainingInstructorPayloadFields = Pick<
  CreateOpenTrainingPayload,
  'instructorType' | 'sessions' | 'externalIndividualInstructors' | 'cnpj' | 'companyName' | 'externalCompanyInstructors'
>;

export function useOpenTrainingInstructor(members: Ref<BranchMember[]> = ref([])) {
  const mode = ref<OpenTrainingInstructorMode | null>(null);
  const externalType = ref<OpenTrainingExternalType | null>(null);

  const internal = useOnboardingMinistrantes(members);
  const externalIndividual = useOpenTrainingExternalIndividualInstructors();
  const externalCompany = useOpenTrainingExternalCompanyInstructors();

  function setMode(value: OpenTrainingInstructorMode): void {
    mode.value = value;
    if (value === 'INTERNAL') {
      externalType.value = null;
    }
  }

  function setExternalType(value: OpenTrainingExternalType): void {
    externalType.value = value;
    externalIndividual.reset();
    externalCompany.reset();
  }

  const isComplete = computed(() => {
    if (mode.value === 'INTERNAL') return internal.isComplete.value;
    if (mode.value === 'EXTERNAL' && externalType.value === 'EXTERNAL_INDIVIDUAL') {
      return externalIndividual.isComplete.value;
    }
    if (mode.value === 'EXTERNAL' && externalType.value === 'EXTERNAL_COMPANY') {
      return externalCompany.isComplete.value;
    }
    return false;
  });

  function buildInternalPayload(): OpenTrainingInstructorPayloadFields | null {
    const sessions: NonNullable<OpenTrainingInstructorPayloadFields['sessions']> = [];
    for (const row of internal.rows.value) {
      const session = buildMinistranteSessionPayload(row);
      if (session === null) return null;
      sessions.push(session);
    }
    return { instructorType: 'INTERNAL', sessions };
  }

  function buildPayload(): OpenTrainingInstructorPayloadFields | null {
    if (mode.value === 'INTERNAL') {
      return buildInternalPayload();
    }

    if (mode.value === 'EXTERNAL' && externalType.value === 'EXTERNAL_INDIVIDUAL') {
      const externalIndividualInstructors = externalIndividual.buildPayload();
      if (externalIndividualInstructors === null) return null;
      return { instructorType: 'EXTERNAL_INDIVIDUAL', externalIndividualInstructors };
    }

    if (mode.value === 'EXTERNAL' && externalType.value === 'EXTERNAL_COMPANY') {
      const companyPayload = externalCompany.buildPayload();
      if (companyPayload === null) return null;
      return {
        instructorType: 'EXTERNAL_COMPANY',
        cnpj: companyPayload.cnpj,
        companyName: companyPayload.companyName,
        externalCompanyInstructors: companyPayload.externalCompanyInstructors,
      };
    }

    return null;
  }

  function applyFieldErrors(fieldErrors: Record<string, string[]>): void {
    externalIndividual.applyFieldErrors(fieldErrors);
    externalCompany.applyFieldErrors(fieldErrors);
  }

  return {
    mode,
    externalType,
    internal,
    externalIndividual,
    externalCompany,
    setMode,
    setExternalType,
    isComplete,
    buildPayload,
    applyFieldErrors,
  };
}
